import { useState } from "react";
import { CircleCheck, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSite } from "@/context/SiteContext";
import { http, formatApiError } from "@/lib/api";

const GOALS = ["Build muscle", "Lose weight", "Get stronger", "General fitness", "Just getting started"];
const TYPES = [{ v: "membership", l: "Membership" }, { v: "guest_pass", l: "Guest Pass" }];
const inputCls = "h-12 rounded-sm border-white/15 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-[var(--gold)]";

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-white/60">{label}</Label>{children}</div>;
}

function Done({ type, onReset, idPrefix }) {
  return (
    <div className="flex flex-col items-start gap-3 py-6" data-testid={`${idPrefix}-form-success`}>
      <CircleCheck className="h-10 w-10 text-[var(--gold)]" />
      <p className="h-display text-3xl text-white">{type === "contact" ? "Message received" : "You're on the list"}</p>
      <p className="text-sm text-white/60">Thanks! Our team will get back to you shortly. Want to talk now? Give either gym a call.</p>
      <button onClick={onReset} className="text-sm text-[var(--gold)] underline-offset-4 hover:underline" data-testid={`${idPrefix}-form-reset`}>Send another</button>
    </div>
  );
}

export function LeadForm({ defaultType = "membership", defaultPlan = "", allowTypeSwitch = false, idPrefix = "lead" }) {
  const { content } = useSite();
  const empty = { name: "", email: "", phone: "", goal: "", location: "", plan: defaultPlan, message: "" };
  const [type, setType] = useState(defaultType);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/leads", { ...form, type, plan: type === "guest_pass" ? "Guest Pass" : form.plan });
      setDone(true);
      toast.success(type === "contact" ? "Thanks! Your message was sent." : "Got it! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Done type={type} idPrefix={idPrefix} onReset={() => { setForm(empty); setDone(false); }} />;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${idPrefix}-form`}>
      {allowTypeSwitch && (
        <div className="grid grid-cols-2 gap-1 rounded-sm border border-white/10 p-1">
          {TYPES.map((t) => (
            <button type="button" key={t.v} onClick={() => setType(t.v)} data-testid={`${idPrefix}-type-${t.v}`}
              className={`rounded-sm py-2 font-display text-base font-bold uppercase tracking-wide transition-colors duration-200 ${type === t.v ? "bg-[var(--gold)] text-black" : "text-white/60 hover:text-white"}`}>{t.l}</button>
          ))}
        </div>
      )}
      <Field label="Full name"><Input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid={`${idPrefix}-name-input`} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email"><Input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid={`${idPrefix}-email-input`} /></Field>
        <Field label="Phone"><Input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(718) 555-0123" data-testid={`${idPrefix}-phone-input`} /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Goal">
          <Select value={form.goal} onValueChange={set("goal")}>
            <SelectTrigger className={inputCls} data-testid={`${idPrefix}-goal-select`}><SelectValue placeholder="Pick a goal" /></SelectTrigger>
            <SelectContent>{GOALS.map((g) => <SelectItem key={g} value={g} data-testid={`${idPrefix}-goal-option-${g.toLowerCase().replace(/\s+/g, "-")}`}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred location">
          <Select value={form.location} onValueChange={set("location")}>
            <SelectTrigger className={inputCls} data-testid={`${idPrefix}-location-select`}><SelectValue placeholder="Pick a gym" /></SelectTrigger>
            <SelectContent>
              {[...content.locations.map((l) => l.name), "Either"].map((n) => <SelectItem key={n} value={n} data-testid={`${idPrefix}-location-option-${n.toLowerCase()}`}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      {type === "membership" && (
        <Field label="Interested in (optional)">
          <Select value={form.plan} onValueChange={set("plan")}>
            <SelectTrigger className={inputCls} data-testid={`${idPrefix}-plan-select`}><SelectValue placeholder="Not sure yet" /></SelectTrigger>
            <SelectContent>{content.plans.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      {type === "contact" && (
        <Field label="Message"><Textarea value={form.message} onChange={set("message")} rows={4} className="rounded-sm border-white/15 bg-white/[0.03] text-white" placeholder="How can we help?" data-testid={`${idPrefix}-message-input`} /></Field>
      )}
      <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-60" data-testid={`${idPrefix}-submit-btn`}>
        {busy && <LoaderCircle className="h-5 w-5 animate-spin" />}
        {type === "contact" ? "Send Message" : type === "guest_pass" ? "Request Guest Pass" : "Get Started"}
      </button>
      <p className="text-center text-xs text-white/40">No payment online. Sales are completed at the front desk.</p>
    </form>
  );
}
